/**
 * Bubble's saved layout, per map, on this device: where each bubble settled
 * (x, y, its radius and parent then, and any overlap accepted as settled).
 *
 * Kept in IndexedDB (`orbital-bubble-positions`, one record per map), not in
 * localStorage: with item ids about 160 characters per item, a map of 27,000
 * items needs 4.3 million characters, and localStorage holds about 5.2
 * million for the whole site (Chromium and Firefox). A save that did not fit
 * marked storage broken, which stops all edits.
 *
 * It is a cache: when it cannot be read or saved, Bubble lays the map out
 * again. Nothing here marks storage broken.
 *
 * Reads are synchronous (the render needs them at once): `loadBubblePositions`
 * fills the memory copy first (interfaces/classic/views/bubble/index.js waits for it). Writes
 * update the memory copy and are merged into the stored record, so a write
 * made before the map's layout was loaded keeps what was stored.
 */

const DB_NAME = "orbital-bubble-positions";
const STORE = "maps";
// Before 2026-10-11 (localStorage); moved over when a map's layout loads.
const LEGACY_PREFIX = "orbital-bubble-pos-";

/** docId → { positions, loaded, changed: Map(id → position) } */
const maps = new Map();
let dbPromise = null;

function openDb() {
  dbPromise ||= new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  }).catch((err) => {
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

function entry(docId) {
  let e = maps.get(docId);
  if (!e) maps.set(docId, (e = { positions: {}, loaded: false, changed: new Map() }));
  return e;
}

function readLegacy(docId) {
  try {
    const raw = localStorage.getItem(LEGACY_PREFIX + docId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function removeLegacy(docId) {
  try { localStorage.removeItem(LEGACY_PREFIX + docId); } catch { /* unavailable */ }
}

/** True once the map's saved layout is in memory (or could not be read). */
export function bubblePositionsLoaded(docId) {
  return !!maps.get(docId)?.loaded;
}

const loading = new Map(); // docId → Promise

/**
 * Read the map's saved layout into memory. Layouts of other maps with no
 * change left to save are dropped from memory.
 * @param {string} docId
 * @returns {Promise<void>}
 */
export function loadBubblePositions(docId) {
  if (!docId || bubblePositionsLoaded(docId)) return Promise.resolve();
  if (!loading.has(docId)) loading.set(docId, load(docId).finally(() => loading.delete(docId)));
  return loading.get(docId);
}

async function load(docId) {
  for (const [id, other] of maps) {
    if (id !== docId && other.loaded && !other.changed.size) maps.delete(id);
  }
  let stored = null;
  try {
    const db = await openDb();
    stored = await new Promise((resolve, reject) => {
      const request = db.transaction(STORE, "readonly").objectStore(STORE).get(docId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("[BubblePositions] Could not read the saved layout of", docId, err);
  }
  // A layout still in localStorage moves over (under anything newer).
  const legacy = readLegacy(docId);
  const e = entry(docId);
  // Positions set while loading are newer than the stored ones.
  e.positions = { ...(legacy || {}), ...(stored || {}), ...e.positions };
  e.loaded = true;
  if (legacy) {
    for (const [id, position] of Object.entries(e.positions)) e.changed.set(id, position);
    queueFlush();
  }
}

/**
 * The map's saved layout, nodeId → { x, y, r?, parentId?, o? }.
 * @param {string} docId
 * @returns {Object}
 */
export function getBubblePositions(docId) {
  return (docId && maps.get(docId)?.positions) || {};
}

/** One bubble's saved position, or null. */
export function getBubblePosition(docId, nodeId) {
  return getBubblePositions(docId)[nodeId] || null;
}

/**
 * Save one bubble's position (a drag, a bubble added from the keyboard).
 * @param {string} docId
 * @param {string} nodeId
 * @param {number} x
 * @param {number} y
 * @param {number} [r] - Radius at the time the layout was saved
 * @param {string} [parentId] - Actual tree parent, even when it is the focused root
 */
export function setBubblePosition(docId, nodeId, x, y, r, parentId) {
  if (!docId || !nodeId) return;
  setBubblePositionsBatch(docId, [{ id: nodeId, x, y, r, parentId }]);
}

/**
 * Save many positions at once (the layout settled).
 * @param {string} docId
 * @param {Array<{id: string, x: number, y: number, r?: number, parentId?: string, o?: number}>} nodePositions
 */
export function setBubblePositionsBatch(docId, nodePositions) {
  if (!docId || !nodePositions?.length) return;
  const e = entry(docId);
  for (const { id, x, y, r, parentId, o } of nodePositions) {
    // Unchanged: not saved again. Bubble saves every position before each
    // render; on a large map that wrote its whole layout at every edit.
    const old = e.positions[id];
    if (old && old.x === x && old.y === y && old.r === r && old.parentId === parentId && (old.o || 0) === (o || 0)) continue;
    const position = { x, y, ...(r !== undefined ? { r } : {}), ...(parentId !== undefined ? { parentId } : {}), ...(o ? { o } : {}) };
    e.positions[id] = position;
    e.changed.set(id, position);
  }
  if (e.changed.size) queueFlush();
}

/** Forget the map's saved layout (the map left this device). */
export async function clearBubblePositions(docId) {
  if (!docId) return;
  maps.delete(docId);
  removeLegacy(docId);
  try {
    const db = await openDb();
    await done(db.transaction(STORE, "readwrite"), (store) => store.delete(docId));
  } catch (err) {
    console.warn("[BubblePositions] Could not delete the saved layout of", docId, err);
  }
}

function done(tx, run) {
  return new Promise((resolve, reject) => {
    run(tx.objectStore(STORE));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

// One save at a time, after the task that set the positions; positions set
// while it runs are saved by the next round.
let saving = null;
let again = false;
function queueFlush() {
  if (saving) { again = true; return; }
  saving = (async () => {
    do {
      again = false;
      await null;
      await flush();
    } while (again);
    saving = null;
  })();
}

async function flush() {
  const pending = [];
  for (const [docId, e] of maps) {
    if (!e.changed.size) continue;
    pending.push([docId, Object.fromEntries(e.changed), e.loaded]);
    e.changed.clear();
  }
  if (!pending.length) return;
  try {
    const db = await openDb();
    for (const [docId, changes, loaded] of pending) {
      await done(db.transaction(STORE, "readwrite"), (store) => {
        // Merged into what is stored: a map whose layout is not loaded here
        // keeps the rest of its layout.
        const request = store.get(docId);
        request.onsuccess = () => store.put({ ...(request.result || {}), ...changes }, docId);
      });
      if (loaded) removeLegacy(docId);
    }
  } catch (err) {
    console.warn("[BubblePositions] Could not save the layout; Bubble lays it out again next time", err);
  }
}

/** Resolves once the positions set so far are saved (checks). */
export function bubblePositionsSaved() {
  return saving || Promise.resolve();
}
